import { formatDueDate, type BusinessLogo, type BusinessLogoVersion, type MyBusiness } from "@teamna/shared";
import { useMemo, useState } from "react";
import {
  Badge,
  BrandMark,
  BusinessMark,
  Button,
  Checkbox,
  ErrorState,
  FileField,
  FormSection,
  InlineError,
  SegmentedControl,
  SkeletonList,
  ToggleRow,
  showToast,
  type MarkBusiness,
} from "../../components/index.ts";
import { useBusinessLogos, useSetActiveLogo, useUploadLogo } from "@teamna/web-client/hooks/use-business-logos.ts";
import { SaveBar } from "../setup/frame.tsx";
import {
  ACCEPTED_LOGO_TYPES,
  buildLogoDraft,
  LogoFileError,
  readLogoFile,
  type LogoDraft,
  type LogoSettings,
  type LogoSource,
} from "./logo-canvas.ts";
import { checkPlaces, type LogoFix, type PlaceId } from "./logo-pixels.ts";
import { AppColour } from "./app-colour.tsx";

/**
 * Settings, Brand: the business logo (prototype `teamna/prototypes/club-logo.html`, decided
 * 2026-10-04).
 *
 * The owner picks one file. The browser makes a full version and a square version, shows the logo
 * in each place people see it, and says in plain words what goes wrong where, with a one-tap fix.
 * Nothing changes for anyone until "Use this logo". Earlier logos stay, so the owner can go back,
 * and "Use initials" goes back to the initials every business starts with.
 */
export function BrandTab({
  business,
  showAppColour = true,
}: {
  business: MyBusiness;
  /** The colour of the Teamna app. Off in the first run, which asks for the logo only. */
  showAppColour?: boolean;
}) {
  const logos = useBusinessLogos(business.businessId);
  const [source, setSource] = useState<LogoSource | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  // A new key after each save or cancel, so the file field forgets the last file name.
  const [pickCount, setPickCount] = useState(0);

  async function pick(file: File) {
    setReadError(null);
    setReading(true);
    try {
      setSource(await readLogoFile(file));
    } catch (error) {
      setReadError(error instanceof LogoFileError ? error.message : "We cannot read this file. Save it as a PNG and try again.");
    } finally {
      setReading(false);
    }
  }

  if (logos.isLoading) return <SkeletonList rows={3} />;
  if (logos.error || !logos.data) {
    return <ErrorState error={logos.error ?? new Error("missing")} onRetry={() => void logos.refetch()} />;
  }

  const live = logos.data.items.find((item) => item.id === logos.data.activeLogoId) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <FormSection
        title="Logo"
        description="People see your logo in Desk, in the member area, on your invite page, on the sign-in screen and at the top of the Teamna app."
      >
        <div className="flex flex-col gap-4 py-4">
          <LiveLogo business={business} live={live} />
          <FileField
            key={pickCount}
            label="New logo"
            hint="PNG, JPG, SVG or WebP. 512 px or larger. 5 MB or less."
            accept={ACCEPTED_LOGO_TYPES}
            buttonLabel={reading ? "Reading the file" : "Choose a file"}
            disabled={reading}
            error={readError ?? undefined}
            fileName={source?.fileName}
            onFile={(file) => void pick(file)}
          />
        </div>
      </FormSection>

      {source ? (
        <LogoEditor
          key={`${source.fileName}-${source.pixels.width}-${source.pixels.height}`}
          business={business}
          source={source}
          showAppColour={showAppColour}
          onDone={() => {
            setSource(null);
            setPickCount((count) => count + 1);
          }}
        />
      ) : null}

      <EarlierLogos business={business} items={logos.data.items} activeLogoId={logos.data.activeLogoId} />

      {/*
        A new key after a save, a business switch or a logo change, so the choice starts again from
        the saved colour and the logo that is live now (the Logo colour option can come or go).
      */}
      {showAppColour ? (
        <AppColour
          key={`${business.businessId}-${business.appAccent ?? "teamna"}-${live?.accent ?? "no-logo-colour"}`}
          business={business}
          logoAccent={live?.accent ?? null}
        />
      ) : null}
    </div>
  );
}

// --- the live logo -------------------------------------------------------------------------------

function LiveLogo({ business, live }: { business: MyBusiness; live: BusinessLogoVersion | null }) {
  const setActive = useSetActiveLogo(business.businessId);
  return (
    <>
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-line bg-surface px-4 py-3">
        <BusinessMark business={{ ...business, logo: live }} size={56} />
        <div className="min-w-[12rem] flex-1">
          <p className="text-[15px] font-semibold text-ink">{live ? "Your logo is live" : "Your initials are live"}</p>
          <p className="text-[13px] text-ink-2">
            {live
              ? `Uploaded on ${formatDueDate(new Date(live.createdAt), business.timezone)}.`
              : "Every business starts with its initials. Upload a logo to show it instead."}
          </p>
        </div>
        {live ? (
          <Button
            variant="secondary"
            loading={setActive.isPending}
            onClick={() =>
              setActive.mutate({ logoId: null }, { onSuccess: () => showToast("Your initials are live.") })
            }
          >
            Use initials
          </Button>
        ) : null}
      </div>
      {/* Under the strip, not in it, so the error is as wide as the logo controls. */}
      {setActive.error ? <InlineError error={setActive.error} /> : null}
    </>
  );
}

// --- the editor --------------------------------------------------------------------------------

const PLACE_TITLES: Record<PlaceId, { title: string; who: string }> = {
  sidebar: { title: "Desk sidebar", who: "You and your team" },
  member: { title: "Member home", who: "Members" },
  dark: { title: "Dark screens", who: "Everyone in dark mode" },
  invite: { title: "Invite page", who: "New members" },
  "sign-in": { title: "Sign-in screen", who: "New members" },
  chips: { title: "Business chips", who: "Members of more than one business" },
};

const FIX_LABELS: Record<LogoFix, string> = {
  background: "Remove the background",
  plate: "Add the white plate",
  left: "Show the left part",
};

function LogoEditor({
  business,
  source,
  onDone,
  showAppColour,
}: {
  business: MyBusiness;
  source: LogoSource;
  onDone: () => void;
  /** The colour section is on this screen, so the editor can point to it. */
  showAppColour: boolean;
}) {
  const [settings, setSettings] = useState<LogoSettings>({ removeBackground: false, plateOnDark: false, focus: "whole" });
  const [rights, setRights] = useState(false);
  const [rightsError, setRightsError] = useState(false);
  const upload = useUploadLogo(business.businessId);

  const built = useMemo((): { draft: LogoDraft } | { error: string } => {
    try {
      return { draft: buildLogoDraft(source, settings) };
    } catch (error) {
      return { error: error instanceof LogoFileError ? error.message : "We cannot read this file. Save it as a PNG and try again." };
    }
  }, [source, settings]);

  if ("error" in built) {
    return (
      <FormSection title="Check your logo">
        <p className="py-4 text-[14px] font-semibold text-cancelled">{built.error}</p>
      </FormSection>
    );
  }
  const { draft } = built;

  const preview: MarkBusiness = {
    businessId: business.businessId,
    name: business.name,
    slug: business.slug,
    logo: {
      id: "draft",
      fullUrl: draft.full.dataUrl,
      markUrl: draft.mark.dataUrl,
      fullWidth: draft.full.width,
      fullHeight: draft.full.height,
      plateOnDark: settings.plateOnDark,
      accent: draft.accent,
    },
  };
  const checks = checkPlaces(draft.facts);
  const good = checks.filter((row) => !row.problem).length;

  function fix(kind: LogoFix) {
    setSettings((current) =>
      kind === "background"
        ? { ...current, removeBackground: true }
        : kind === "plate"
          ? { ...current, plateOnDark: true }
          : { ...current, focus: "left" },
    );
  }

  function save() {
    if (!rights) {
      setRightsError(true);
      return;
    }
    upload.mutate(
      {
        full: draft.full.base64,
        mark: draft.mark.base64,
        plateOnDark: settings.plateOnDark,
        accent: draft.accent,
        rightsConfirmed: true,
      },
      {
        onSuccess: () => {
          showToast("Your logo is live. People see it the next time they open Teamna.");
          onDone();
        },
      },
    );
  }

  return (
    <FormSection
      title="Check your logo"
      description="Nobody sees this logo until you press Use this logo. Fix what the checks find, or use it as it is."
    >
      <div className="flex flex-col gap-4 py-4">
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
          <figure className="m-0 flex flex-col gap-1.5">
            <figcaption className="eyebrow text-ink-2">Full logo</figcaption>
            <div className="logo-checker flex h-32 items-center justify-center rounded-xl border border-line p-3">
              <img src={draft.full.dataUrl} alt="Full logo" className="max-h-full max-w-full object-contain" />
            </div>
          </figure>
          <figure className="m-0 flex flex-col gap-1.5">
            <figcaption className="eyebrow text-ink-2">Square version</figcaption>
            <div className="logo-checker size-32 overflow-hidden rounded-xl border border-line">
              <img src={draft.mark.dataUrl} alt="Square version" className="size-full" />
            </div>
          </figure>
        </div>

        {draft.wide ? (
          <SegmentedControl
            label="The square shows"
            options={[
              { value: "whole", label: "Whole logo" },
              { value: "left", label: "Left part" },
              { value: "middle", label: "Middle part" },
            ]}
            value={settings.focus}
            onChange={(focus) => setSettings((current) => ({ ...current, focus }))}
          />
        ) : null}

        <div className="flex flex-col">
          <ToggleRow
            label="Remove the background"
            hint={
              draft.boxFound
                ? "We remove the box around your logo. A white part inside your logo stays."
                : "Your logo has no box. There is nothing to remove."
            }
            checked={settings.removeBackground}
            disabled={!draft.boxFound}
            onChange={(removeBackground) => setSettings((current) => ({ ...current, removeBackground }))}
          />
          <ToggleRow
            label="White plate on dark screens"
            hint="A dark logo sits on a small white plate when the screen is dark."
            checked={settings.plateOnDark}
            onChange={(plateOnDark) => setSettings((current) => ({ ...current, plateOnDark }))}
          />
        </div>

        <p className="flex items-center gap-2.5 text-[13px] text-ink-2">
          <span
            aria-hidden="true"
            className="size-5 shrink-0 rounded-md border border-line"
            style={{ backgroundColor: draft.accent ?? "var(--color-accent)" }}
          />
          {draft.accent
            ? `The colour of your logo. We use it for thin lines and for the greeting on your Home, made dark enough to read.${showAppColour ? " To use it for buttons in the Teamna app, choose Logo colour below." : ""}`
            : "Your logo has no strong colour. Thin lines and your Home greeting use Teamna blue."}
        </p>

        <div className="flex flex-wrap items-baseline justify-between gap-2 pt-2">
          <h3 className="text-[17px] font-semibold text-ink">How people see it</h3>
          <p className="text-[13px] text-ink-2" aria-live="polite">
            {good === checks.length ? `All ${checks.length} places look good.` : `${good} of ${checks.length} places look good.`}
          </p>
        </div>
        <ul className="grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3" data-testid="logo-places">
          {checks.map(({ place, problem }) => (
            <li
              key={place}
              data-place={place}
              className={`flex flex-col overflow-hidden rounded-xl border bg-paper ${problem ? "border-late" : "border-line"}`}
            >
              <div className="h-[140px] overflow-hidden border-b border-line">
                <PlacePreview place={place} business={preview} />
              </div>
              <div className="flex flex-1 flex-col gap-1.5 px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[14px] font-semibold text-ink">{PLACE_TITLES[place].title}</p>
                  {problem ? <Badge tone="late">Needs a fix</Badge> : <Badge tone="confirmed">Looks good</Badge>}
                </div>
                <p className="text-[12px] text-ink-2">{PLACE_TITLES[place].who}</p>
                {problem ? <p className="text-[13px] font-semibold text-late-ink">{problem.message}</p> : null}
                {problem?.fix ? (
                  <div className="mt-auto pt-1">
                    <Button variant="secondary" onClick={() => fix(problem.fix!)}>
                      {FIX_LABELS[problem.fix]}
                    </Button>
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>

        <Checkbox
          label="I own this logo, or I have permission to use it"
          checked={rights}
          error={rightsError && !rights ? "Confirm that you can use this logo." : undefined}
          onChange={(next) => {
            setRights(next);
            if (next) setRightsError(false);
          }}
        />

        <SaveBar note={good < checks.length ? "You can use the logo now and fix it later." : undefined}>
          <Button loading={upload.isPending} onClick={save}>
            Use this logo
          </Button>
          <Button variant="secondary" onClick={onDone}>
            Cancel
          </Button>
        </SaveBar>
        {upload.error ? <InlineError error={upload.error} /> : null}
      </div>
    </FormSection>
  );
}

/** A small copy of each place, drawn with the real mark and the real tokens. */
function PlacePreview({ place, business }: { place: PlaceId; business: MarkBusiness }) {
  const logo = business.logo as BusinessLogo;
  switch (place) {
    case "sidebar":
      return (
        <div className="flex h-full flex-col gap-1 bg-paper px-4 py-4">
          <div className="flex items-center gap-2.5">
            <BusinessMark business={business} size={36} />
            <div className="min-w-0">
              <p className="truncate text-[14px] font-semibold text-ink">{business.name}</p>
              <p className="text-[12px] text-ink-3">Owner office</p>
            </div>
          </div>
          <p className="mt-2 rounded-lg bg-accent-surface px-3 py-1.5 text-[13px] font-semibold text-accent-strong">Home</p>
          <p className="px-3 py-1 text-[13px] font-semibold text-ink-2">Schedule</p>
        </div>
      );
    case "member":
      return (
        <div className="flex h-full flex-col bg-surface px-4 pt-5">
          <div className="mb-2 flex min-w-0 items-center gap-2">
            <BusinessMark business={business} size={28} />
            <p className="eyebrow min-w-0 truncate">{business.name}</p>
          </div>
          <p className="text-[26px] font-semibold text-ink">Home</p>
        </div>
      );
    case "dark":
      return (
        <div data-tone="dark" className="flex h-full flex-col justify-center gap-2 bg-paper px-4">
          <div className="flex items-center gap-2.5">
            <BusinessMark business={business} size={40} />
            <p className="min-w-0 truncate text-[14px] font-semibold text-ink">{business.name}</p>
          </div>
          <p className="text-[12px] text-ink-2">A screen in dark mode</p>
        </div>
      );
    case "invite":
      return (
        <div
          className="flex h-full flex-col items-center gap-2 border-t-4 bg-paper px-3 pt-3 text-center"
          style={{ borderTopColor: logo.accent ?? "var(--color-accent)" }}
        >
          <img src={logo.fullUrl} alt="" className="h-12 w-auto max-w-[80%] object-contain" />
          <p className="text-[13px] font-semibold text-ink">You have been invited to join {business.name}</p>
          <p className="mt-auto flex items-center gap-1.5 pb-2 text-[11px] text-ink-2">
            <BrandMark className="size-4" />
            Powered by Teamna
          </p>
        </div>
      );
    case "sign-in":
      return (
        <div className="flex h-full flex-col gap-2 bg-paper px-4 pt-4">
          <img src={logo.fullUrl} alt="" className="h-11 w-auto max-w-[70%] object-contain object-left" />
          <p className="rounded-lg bg-accent-surface px-3 py-2 text-[13px] font-semibold text-accent-strong">
            You are joining {business.name}
          </p>
        </div>
      );
    case "chips":
      return (
        <div className="flex h-full flex-col justify-center gap-2 bg-paper px-4">
          <p className="eyebrow text-ink-2">Which business</p>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex h-9 items-center gap-1.5 rounded-pill border border-accent bg-accent pr-3.5 pl-1.5 text-[13px] font-semibold text-inverse">
              <BusinessMark business={business} size={24} />
              <span className="max-w-[9rem] truncate">{business.name}</span>
            </span>
            <span className="inline-flex h-9 items-center gap-1.5 rounded-pill border border-line bg-paper pr-3.5 pl-1.5 text-[13px] font-semibold text-ink-2">
              <BusinessMark business={{ businessId: "another", name: "Another business" }} size={24} />
              Another
            </span>
          </div>
        </div>
      );
  }
}

// --- earlier logos -------------------------------------------------------------------------------

function EarlierLogos({
  business,
  items,
  activeLogoId,
}: {
  business: MyBusiness;
  items: BusinessLogoVersion[];
  activeLogoId: string | null;
}) {
  const setActive = useSetActiveLogo(business.businessId);
  const earlier = items.filter((item) => item.id !== activeLogoId);
  if (earlier.length === 0) return null;
  return (
    <FormSection title="Earlier logos" description="Go back to a logo you used before.">
      <div className="flex flex-col gap-3 py-4">
        <ul className="grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3">
          {earlier.map((item) => (
            <li key={item.id} className="flex items-center gap-3 rounded-xl border border-line bg-paper p-3">
              <BusinessMark business={{ ...business, logo: item }} size={48} />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] text-ink-2">Uploaded on {formatDueDate(new Date(item.createdAt), business.timezone)}</p>
              </div>
              <Button
                variant="secondary"
                disabled={setActive.isPending}
                onClick={() =>
                  setActive.mutate({ logoId: item.id }, { onSuccess: () => showToast("That logo is live again.") })
                }
              >
                Use again
              </Button>
            </li>
          ))}
        </ul>
        {setActive.error ? <InlineError error={setActive.error} /> : null}
      </div>
    </FormSection>
  );
}
